import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useParams } from 'react-router';

import * as playlistActions from '@/playlist/Actions';
import { FRONTEND_ROUTES } from '@/services/Constants';
import { getFreePlayCategoryPath, getFreePlayExercisePath } from '@/pages/freePlay/Helpers';
import useGameLayout from '@/playlist/games/common/hooks/useGameLayout';
import useBatteryLevel from '@/playlist/games/timeGame/hooks/useBatteryLevel';
import usePanelTransition from '@/playlist/games/timeGame/hooks/usePanelTransition';
import useTimeGameQuestion from '@/playlist/games/timeGame/hooks/useTimeGameQuestion';
import useAudioPlayer from '@/services/hocs/useAudioPlayer';
import useResizeKey from '@/services/hocs/useResizeKey';
import { TimeQuestionType } from '@/playlist/games/timeGame/types';
import PlaylistExitContext from '@/playlist/PlaylistExitContext';
import { EndOutcome, PlayList, Question } from '@/playlist/types';
import { historyPush, historyReplace } from '@/services/History';

import TimeGameHeader from '@/playlist/games/timeGame/components/common/TimeGameHeader';
import EndScreen from '@/playlist/games/timeGame/components/stages/EndScreen';
import GameScreen from '@/playlist/games/timeGame/components/stages/GameScreen';
import StartScreen from '@/playlist/games/timeGame/components/stages/StartScreen';

import timeGameMusic from '@/playlist/games/timeGame/assets/sounds/music.ogg';

import '@/playlist/games/timeGame/styles/time-game.scss';

type SelectedTime = {
	hours: number;
	minutes: number;
};

const MAX_BATTERY_LEVEL = 5;
const MIN_BATTERY_LEVEL = 0;
const DEFAULT_SELECTED_TIME: SelectedTime = { hours: 0, minutes: 15 };

type TimeGameProps = {
	playList: PlayList | null;
	levelProgress?: any;
	isBridgePlaylist: boolean;
	sendAnswerRequest: typeof playlistActions.sendAnswerRequest;
	getPlaylistSuccess: typeof playlistActions.getPlaylistSuccess;
	finishPlaylistRequest: typeof playlistActions.finishPlaylistRequest;
	questions: Question[];
};

const PANEL_TRANSITION_DURATION = 0.65;

const TimeGame = ({
	playList,
	levelProgress,
	isBridgePlaylist,
	sendAnswerRequest,
	getPlaylistSuccess,
	finishPlaylistRequest,
	questions,
}: TimeGameProps) => {
	useGameLayout();

	const onClose = useContext(PlaylistExitContext);
	const resizeKey = useResizeKey();
	const [hasGameStarted, setHasGameStarted] = useState<boolean>(false);
	const [endOutcome, setEndOutcome] = useState<EndOutcome | null>(null);
	const [answers, setAnswers] = useState<boolean[]>([]);
	const [questionQueue, setQuestionQueue] = useState<number[]>(() =>
		questions ? questions.map((_, index) => index) : [],
	);
	const [selectedTime, setSelectedTime] = useState<SelectedTime>(DEFAULT_SELECTED_TIME);
	const [questionStartTs, setQuestionStartTs] = useState<number>(() => Date.now());
	// Track number of attempts per question (0 = first attempt, 1 = second attempt)
	const [questionAttempts, setQuestionAttempts] = useState<Record<number, number>>({});
	const { startPanelRef, gamePanelRef, transitionToGame, shouldRenderStartPanel, resetPanels } =
		usePanelTransition({ duration: PANEL_TRANSITION_DURATION });
	const { play: playBackgroundMusic, stop: stopBackgroundMusic } = useAudioPlayer({
		src: timeGameMusic,
		loop: true,
		volume: 0.3,
	});

	// Battery level management
	const { batteryLevel, increaseBattery, decreaseBattery, resetBattery } = useBatteryLevel();

	const { categoryId: categoryIdFromRoute, category: categorySlugFromRoute } = useParams<{
		categoryId?: string;
		category?: string;
	}>();

	const freePlayCategoryId = useMemo(() => {
		if (categoryIdFromRoute != null && !Number.isNaN(Number(categoryIdFromRoute))) {
			return Number(categoryIdFromRoute);
		}
		return undefined;
	}, [categoryIdFromRoute]);

	const freePlayCategoryPath = useMemo(() => {
		if (freePlayCategoryId != null && categorySlugFromRoute) {
			return getFreePlayCategoryPath(freePlayCategoryId, categorySlugFromRoute);
		}
		return FRONTEND_ROUTES.HOME;
	}, [freePlayCategoryId, categorySlugFromRoute]);

	const totalQuestions = questions?.length || 0;
	const currentQuestionQueueIndex = questionQueue[0];
	const currentQuestion =
		currentQuestionQueueIndex != null && questions
			? questions[currentQuestionQueueIndex]
			: undefined;
	const currentAlienPosition = questionQueue.length > 0 ? 0 : -1;
	const correctAnswersCount = answers.filter((a) => a).length;

	const starProgressData = useMemo(() => {
		if (!levelProgress) {
			return null;
		}

		const {
			level_completed,
			level_stars_earned,
			stars_earned: progressStarsEarned,
			current_level,
		} = levelProgress;

		const totalStars = level_completed
			? level_completed?.required_stars
			: current_level?.required_stars;

		const levelCompletedRequiredStars = level_completed?.required_stars;
		const rawEarned = level_stars_earned;
		const earnedStars =
			levelCompletedRequiredStars && rawEarned > levelCompletedRequiredStars
				? levelCompletedRequiredStars
				: rawEarned;

		const previousEarned = level_stars_earned - progressStarsEarned;

		return {
			progressStarsEarned,
			totalStars,
			earnedStars,
			previousEarned: Math.max(0, previousEarned),
		};
	}, [levelProgress]);

	useEffect(() => {
		if (!questions?.length || hasGameStarted) {
			return;
		}
		setQuestionQueue(questions.map((_, index) => index));
	}, [questions, hasGameStarted]);
	const needsDigitalAnswer = (currentQuestion?.type || '').includes('ToDigital');

	useEffect(() => {
		if (hasGameStarted && currentQuestion?.id != null) {
			setQuestionStartTs(Date.now());
		}
	}, [currentQuestion?.id, hasGameStarted]);

	useEffect(() => {
		if (endOutcome !== null) {
			stopBackgroundMusic();
			return;
		}
		playBackgroundMusic();
		return () => {
			stopBackgroundMusic();
		};
	}, [endOutcome, playBackgroundMusic, stopBackgroundMusic]);

	const handleAnswerSubmitted = useCallback(
		(isCorrect: boolean, isSecondAttempt: boolean) => {
			setAnswers((prev) => [...prev, isCorrect]);

			const batteryDelta = isCorrect ? 1 : -1;
			const projectedBatteryLevel = Math.min(
				Math.max(batteryLevel + batteryDelta, MIN_BATTERY_LEVEL),
				MAX_BATTERY_LEVEL,
			);

			if (isCorrect) {
				increaseBattery();
			} else {
				decreaseBattery();
			}

			// Move to next question or end game after a brief delay
			setTimeout(() => {
				// Check if battery is depleted (game lost)
				if (projectedBatteryLevel <= MIN_BATTERY_LEVEL) {
					setEndOutcome(EndOutcome.LOST);
					return;
				}

				setQuestionQueue((prevQueue) => {
					if (prevQueue.length === 0) {
						return prevQueue;
					}

					const [currentIndex, ...rest] = prevQueue;
					let updatedQueue: number[];

					// Remove from queue if: correct OR second attempt (even if wrong)
					if (isCorrect || isSecondAttempt) {
						updatedQueue = rest;
					} else {
						// First attempt wrong: move to end of queue
						updatedQueue = rest.length > 0 ? [...rest, currentIndex] : [currentIndex];
					}

					if (updatedQueue.length === 0) {
						setEndOutcome(EndOutcome.SUCCEEDED);
					}

					return updatedQueue;
				});
			}, 100);
		},
		[answers, batteryLevel, decreaseBattery, increaseBattery, totalQuestions],
	);

	// Custom hook for answer submission
	const { submitAnswer, checkAnswer } = useTimeGameQuestion({
		playList,
		currentQuestion,
		sendAnswerRequest,
		questionStartTs,
		onAnswerSubmitted: handleAnswerSubmitted,
		freePlayCategoryId,
	});

	// Handle game start
	const handleStart = useCallback(() => {
		setHasGameStarted(true);
		transitionToGame();
	}, [transitionToGame]);

	// Handle back navigation
	const handleBack = useCallback(() => {
		if (onClose) {
			onClose();
			return;
		}
		if (freePlayCategoryPath) {
			historyPush(freePlayCategoryPath);
		} else {
			historyPush(FRONTEND_ROUTES.HOME);
		}
	}, [onClose, freePlayCategoryPath]);

	// Handle game replay
	const handleReplay = useCallback(() => {
		setHasGameStarted(false);
		setSelectedTime({ ...DEFAULT_SELECTED_TIME });
		setQuestionQueue(questions ? questions.map((_, index) => index) : []);
		setQuestionAttempts({});
		setAnswers([]);
		resetPanels();
		resetBattery();
		setEndOutcome(null);

		const newUserLearningPathSection = levelProgress?.user_learning_path_section;
		const categoryId = categoryIdFromRoute;
		const categoryName = categorySlugFromRoute;

		if (newUserLearningPathSection && categoryId && categoryName) {
			getPlaylistSuccess(newUserLearningPathSection);
			const newPath = getFreePlayExercisePath(
				categoryName,
				categoryId,
				'qs-ruimtestation',
				newUserLearningPathSection.id,
			);
			historyReplace(newPath, { autostart: true });
		} else {
			handleBack();
		}
	}, [
		levelProgress,
		categoryIdFromRoute,
		categorySlugFromRoute,
		handleBack,
		getPlaylistSuccess,
		resetPanels,
		questions,
		resetBattery,
	]);

	const handleTimeChange = useCallback((hours: number, minutes: number) => {
		setSelectedTime({ hours, minutes });
	}, []);

	// Wrapper for checkAnswer that uses current selectedTime
	const handleCheckAnswer = useCallback(() => {
		const isAnalogAnswer = !needsDigitalAnswer;
		const isAnalogQuestion = (currentQuestion?.type || '').includes(
			TimeQuestionType.AnalogToDigital,
		);
		return checkAnswer(selectedTime, isAnalogAnswer, isAnalogQuestion);
	}, [selectedTime, needsDigitalAnswer, currentQuestion, checkAnswer]);

	// Handle answer submission via console
	const handleConsoleLaunch = useCallback(() => {
		if (!currentQuestion || !hasGameStarted || questionQueue.length === 0) {
			return;
		}

		const currentIndex = questionQueue[0];
		const attemptCount = questionAttempts[currentIndex] || 0;
		const isAnalogAnswer = !needsDigitalAnswer;
		const isAnalogQuestion = (currentQuestion.type || '').includes('AnalogToDigital');
		const isSecondAttempt = attemptCount > 0;

		// Check if answer is correct
		const isCorrect = checkAnswer(selectedTime, isAnalogAnswer, isAnalogQuestion);

		// Determine if we should send the API request
		// Send request if: correct on first attempt OR any second attempt
		const shouldSendRequest = isCorrect || isSecondAttempt;
		const batteryDelta = isCorrect ? 1 : -1;
		const projectedBatteryLevel = Math.min(
			Math.max(batteryLevel + batteryDelta, MIN_BATTERY_LEVEL),
			MAX_BATTERY_LEVEL,
		);
		const willFinishOutcome =
			projectedBatteryLevel <= MIN_BATTERY_LEVEL
				? EndOutcome.LOST
				: shouldSendRequest && questionQueue.length === 1
				? EndOutcome.SUCCEEDED
				: null;
		const shouldFinish = willFinishOutcome && playList?.id != null;
		const finishCallback = shouldFinish
			? () => finishPlaylistRequest(playList.id, freePlayCategoryId, willFinishOutcome)
			: undefined;

		// Determine which callback to pass based on shouldSendRequest
		const onRequestCompleted = shouldSendRequest ? finishCallback : undefined;
		const onFinish = !shouldSendRequest && answers.length > 0 ? finishCallback : undefined;

		// Update attempt count for first wrong attempts
		if (!shouldSendRequest) {
			setQuestionAttempts((prev) => ({ ...prev, [currentIndex]: attemptCount + 1 }));
		}

		// Submit answer (will only send API request if shouldSendRequest is true)
		submitAnswer(
			selectedTime,
			isAnalogAnswer,
			shouldSendRequest,
			isSecondAttempt,
			onRequestCompleted,
			onFinish,
		);

		// In bridge playlists, sendAnswerRequest saga skips the callback.
		// Dispatch finishPlaylistRequest directly when shouldSendRequest is true
		// (the onFinish path already handles the shouldSendRequest=false case).
		if (shouldFinish && isBridgePlaylist && shouldSendRequest) {
			finishPlaylistRequest(playList!.id, freePlayCategoryId, willFinishOutcome!);
		}
	}, [
		currentQuestion,
		hasGameStarted,
		needsDigitalAnswer,
		selectedTime,
		submitAnswer,
		checkAnswer,
		questionQueue,
		questionAttempts,
		batteryLevel,
		playList?.id,
		freePlayCategoryId,
		isBridgePlaylist,
		finishPlaylistRequest,
		answers,
	]);

	// Show end screen
	if (endOutcome !== null) {
		const wrongAnswersCount = answers.filter((a) => !a).length;
		const currentLevel = levelProgress?.current_level;

		return (
			<EndScreen
				outcome={endOutcome}
				onClose={handleBack}
				onReplay={handleReplay}
				progressStarsEarned={starProgressData?.progressStarsEarned}
				totalStars={starProgressData?.totalStars}
				earnedStars={starProgressData?.earnedStars}
				previousEarned={starProgressData?.previousEarned}
				levelImage={currentLevel?.level_image}
				levelName={currentLevel?.level_name}
				levelTitle={currentLevel?.level_title}
				levelCompleted={levelProgress?.level_completed}
				totalQuestions={totalQuestions}
				wrongAnswers={wrongAnswersCount}
			/>
		);
	}

	// Main game screen
	if (!currentQuestion) {
		return null;
	}

	return (
		<>
			<TimeGameHeader onBack={handleBack} />
			<div className="time-game-stage">
				{shouldRenderStartPanel ? (
					<div
						className="time-game-stage__panel time-game-stage__panel--start"
						ref={startPanelRef}
					>
						<StartScreen onStart={handleStart} onBack={handleBack} />
					</div>
				) : null}

				<div
					className="time-game-stage__panel time-game-stage__panel--game"
					ref={gamePanelRef}
				>
					<GameScreen
						key={resizeKey}
						currentQuestion={currentQuestion}
						currentQuestionIndex={currentQuestionQueueIndex ?? 0}
						currentAlienPosition={currentAlienPosition}
						totalQuestions={totalQuestions}
						correctAnswersCount={correctAnswersCount}
						questionOrder={questionQueue}
						onLaunch={handleConsoleLaunch}
						onTimeChange={handleTimeChange}
						checkAnswer={handleCheckAnswer}
						onBack={handleBack}
						initialHours={selectedTime.hours}
						initialMinutes={selectedTime.minutes}
						resetKey={currentQuestion.id}
						batteryLevel={batteryLevel}
						hasGameStarted={hasGameStarted}
					/>
				</div>
			</div>
		</>
	);
};

function mapStateToProps({ playlistReducer, freePlayReducer }: any) {
	return {
		playList: playlistReducer.playList,
		levelProgress: freePlayReducer.levelProgress,
		questions: playlistReducer.questions,
		isBridgePlaylist: playlistReducer.isBridgePlaylist,
	};
}

const mapDispatchToProps = {
	sendAnswerRequest: playlistActions.sendAnswerRequest,
	getPlaylistSuccess: playlistActions.getPlaylistSuccess,
	finishPlaylistRequest: playlistActions.finishPlaylistRequest,
};

export default connect(mapStateToProps, mapDispatchToProps)(TimeGame);
